/* eslint-disable @intercom/intercom/no-bare-strings */
/* RESPONSIBLE TEAM: team-phone */
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import Service, { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import { Call, Device } from '@twilio/voice-sdk';
import type { RTCSample } from '@twilio/voice-sdk';
import { type TwilioError } from '@twilio/voice-sdk/es5/twilio/errors';
import CallQualityTracker from 'embercom/utils/call-quality-tracker';
import { isValidE164PhoneNumber } from 'embercom/utils/phone-number';
import Ember from 'ember';
import { type TaskGenerator, timeout } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import type IntlService from 'ember-intl/services/intl';
import ENV from 'embercom/config/environment';
import { ajaxDelete, get, post } from 'embercom/lib/ajax';
import type PhoneNumber from 'embercom/models/calling-phone-number';
import { InboxCategory } from 'embercom/models/data/inbox/inbox-categories';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import Metrics from 'embercom/models/metrics';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import CallParticipantSummary from 'embercom/objects/inbox/callings/call-participant-summary';
import Conversation, { type ConversationWireFormat } from 'embercom/objects/inbox/conversation';
import type TeamSummary from 'embercom/objects/inbox/team-summary';
import type User from 'embercom/objects/inbox/user';
import UserSummary from 'embercom/objects/inbox/user-summary';
import PhoneCall from 'embercom/objects/phone/phone-call';
import type AdminAwayService from 'embercom/services/admin-away-service';
import type InboxApi from 'embercom/services/inbox-api';
import type InboxState from 'embercom/services/inbox-state';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type LogService from 'embercom/services/log-service';
import type Session from 'embercom/services/session';
import type Snackbar from 'embercom/services/snackbar';
import { type Notification } from 'embercom/services/snackbar';
import moment from 'moment-timezone';
import {
  type InboundCallbackRequestEvent,
  type ConferenceParticipantAddedEvent,
  type ConferenceParticipantRemovedEvent,
  type ConferenceParticipantFailedToJoinEvent,
  type WarmTransferAddedEvent,
  type WarmTransferFailedEvent,
  type WarmTransferCompletedEvent,
  type WarmTransferToTeamCallingEvent,
  type WarmTransferToTeamFailedEvent,
} from './nexus';
import type Inbox2AssigneeSearch from './inbox2-assignee-search';
import type InboxSearchSuggestionsService from './inbox-search-suggestions-service';
import type ParticipantUserSummary from 'embercom/objects/inbox/participant-user-summary';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

const PHONE_NOTIFICATION_ROUTE_PREFIXES = [
  'inbox.workspace.inbox',
  'inbox.workspace.search',
  'inbox.workspace.dashboard',
];

const ONE_MINUTE = ENV.APP._1M;
const TWO_MINUTES = ONE_MINUTE * 2;
const PHONE_HELP_DESK_ONLY_NOTIFICATION_DISMISSED = 'phone-help-desk-only-notification-dismissed';
const NOTIFICATION_TIMEOUT = 30000;
const WARM_TRANSFER_NOTIFICATION_TIMEOUT = 6000;
const MANUAL_ANSWER_NOTIFICATION_TIMEOUT = 6000;
const MAX_RECOVERY_ATTEMPTS = 5;
const INITIAL_RECOVERY_DELAY_MS = 5000;
const MAX_RECOVERY_DELAY_MS = 60000;

export default class TwilioService extends Service {
  @tracked shouldNotifyTeammate = false;
  @tracked conversation?: Conversation;
  @tracked userId?: string;
  @tracked oldAdminId?: string;
  @tracked oldTeamId: string | undefined = undefined;
  @tracked currentCallAction?:
    | 'transferring-to-teammate'
    | 'warm-transferring-to-teammate'
    | 'transferring-externally'
    | 'warm-transferring-externally'
    | 'transferring-to-workflow'
    | 'adding-participant';
  @tracked addingCallParticipantInfo?: { identifier: string | number; label: string };
  @tracked isOnHold = false;
  @tracked disableHold = false;
  @tracked isRecordingEnabled = false;
  @tracked isRecordingEnabledInbound = false;
  @tracked isRecordingEnabledOutbound = false;
  @tracked isRecording = false;
  @tracked isInitialized = false;
  @tracked workspacePhoneNumber: string | undefined = undefined;
  @tracked calledNumberCountryCode: string | undefined = undefined;
  @tracked unregisteredNotification?: Notification;
  @tracked noPermissionNotification?: Notification;
  @tracked hasActivePhoneNumbers = false;
  @tracked isListening = false;
  @tracked incomingCallType?:
    | 'callback'
    | 'conference'
    | 'transfer'
    | 'warm-transfer'
    | 'phone-call';
  @tracked callerPhone?: string;
  @tracked callerName?: string;
  @tracked incomingConversationId?: number;
  @tracked resolvedOutboundNumber?: string;
  @tracked acceptedCallback = true;
  @tracked callbackAdminId?: number;
  @tracked callbackAcceptanceTimer: NodeJS.Timeout | null = null;
  @tracked addingParticipantTimer: NodeJS.Timeout | null = null;
  @tracked deviceStatusPoller: ReturnType<typeof setTimeout> | null = null;
  @tracked adminLacksMicrophonePermissions = false;
  @tracked participants: CallParticipantSummary[] = [];
  @tracked windowFocusOnlineAbortController?: AbortController;
  @tracked deviceDestroyReason?: 'navigation';
  @tracked warmTransferState?: 'ringing' | 'talking' | 'failed' | 'background-hold';
  @tracked warmTransferStartTime?: number;
  @tracked warmTransferUser?: UserSummary;
  @tracked warmTransferTeamId?: number;
  @tracked warmTransferToTeamWaiting = false;
  @tracked warmTransferToTeamCallingInfo?: {
    adminId: number;
    startedAt: number;
    timeoutSeconds: number;
  };
  @tracked warmTransferToTeamStatus?: {
    type: 'declined' | 'no-answer';
    adminId: number;
  };
  @tracked warmTransferToTeamWaitingTimer: NodeJS.Timeout | null = null;
  @tracked devicePresenceHealthNotification?: Notification;
  @tracked lastSuccessfulDevicePresence?: number;
  @tracked devicePresenceStartTime?: number;
  // Device presence diagnostics tracking
  private devicePresenceSuccessCount = 0;
  private devicePresenceFailureCount = 0;
  private consecutiveFailures = 0;
  private lastDevicePresenceError?: string;
  private lastUserActivityAt?: number;
  private tabHiddenSince?: number;
  private consecutiveHiddenHeartbeats = 0;
  private diagnosticsListenersSetup = false;
  private callIncomingTimestamp?: number;
  private callQualityTracker?: CallQualityTracker;
  private recoveryAttempts = 0;
  private recoveryTimer?: ReturnType<typeof setTimeout>;
  private isRecovering = false;
  private conversationLoadPromise?: Promise<void>;
  @service declare snackbar: Snackbar;
  @service declare customerService: any;
  @service declare session: Session;
  @service declare intercomEventService: any;
  @service declare inboxState: InboxState;
  @service declare inboxApi: InboxApi;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare intercomCallService: IntercomCallService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare adminAwayService: AdminAwayService;
  @service declare logService: LogService;
  @service declare notificationsService: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  @tracked activeCall?: PhoneCall;
  incomingCall: Call | null = null;
  device: Device | null = null;
  callbackAudio: HTMLAudioElement = new window.Audio(assetUrl('/assets/audio/incoming.mp3'));
  isTabManuallyAnswering = false;

  get isTransferToTeammate() {
    return this.currentCallAction === 'transferring-to-teammate';
  }

  get isWarmTransferToTeammate() {
    return this.currentCallAction === 'warm-transferring-to-teammate';
  }

  get isWarmTransferToExternalNumber() {
    return this.currentCallAction === 'warm-transferring-externally';
  }

  get isWarmTransfer() {
    return (
      this.isWarmTransferToTeammate ||
      this.isWarmTransferToExternalNumber ||
      this.warmTransferTeamId !== undefined
    );
  }

  get isTransferToExternalNumber() {
    return this.currentCallAction === 'transferring-externally';
  }

  get isAddingParticipant() {
    return this.currentCallAction === 'adding-participant';
  }

  get isIncomingCallback() {
    return this.incomingCallType === 'callback';
  }

  get isIncomingCallConference() {
    return this.incomingCallType === 'conference';
  }

  get isIncomingCallTransfer() {
    return this.incomingCallType === 'transfer';
  }

  get isIncomingCallWarmTransfer() {
    return this.incomingCallType === 'warm-transfer';
  }

  get userSummary() {
    let user = this.conversation?.userSummary;

    if (this.userId) {
      // First try to find in current call participants (includes externally added users)
      let callParticipant = this.participants.find((p) => p.id === this.userId);
      if (callParticipant?.isUser) {
        return callParticipant.userParticipant;
      }

      // Fallback to conversation participants (original participants)
      return this.conversation?.participantSummaries.findBy('id', this.userId) ?? user;
    }

    // Fast modal: provide a placeholder from call parameters while conversation loads
    if (!user && this.callerPhone) {
      return new UserSummary(
        'pending',
        undefined,
        this.callerName || undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        this.callerPhone,
      );
    }

    return user;
  }

  get isPhoneRoute() {
    return PHONE_NOTIFICATION_ROUTE_PREFIXES.some((routePrefix) =>
      this.router.currentRouteName?.startsWith(routePrefix),
    );
  }

  async initialize() {
    try {
      this.deviceDestroyReason = undefined;

      this.setupWindowListeners();

      let callingSettings = await get(`/ember/inbox/calling_settings`, {
        app_id: this.session.workspace.id,
      });
      this.isRecordingEnabledInbound = callingSettings?.recording_enabled;
      this.isRecordingEnabledOutbound = callingSettings?.recording_enabled_outbound;
      this.isRecordingEnabled = this.isRecordingEnabledInbound;
      this.isRecording = this.isRecordingEnabled;
      this.hasActivePhoneNumbers =
        callingSettings.phone_numbers.filter((number: PhoneNumber) =>
          ['active', 'missing_bundle'].includes(number.status),
        ).length > 0;

      if (!this.hasActivePhoneNumbers) {
        return; // If there are no active phone numbers, we don't need to initialize the Twilio services
      }

      await this.checkAdminMicrophonePermissions();

      if (this.adminLacksMicrophonePermissions) {
        this.noPermissionNotification = this.snackbar.notify(
          this.intl.t('calling.incoming-phone-call-modal.no-permissions-set'),
          {
            type: 'error',
            persistent: true,
            clearable: true,
          },
        );
      }

      await this.setupDevice();
      this.isInitialized = true;
      this.dispatchCallingStateToReact();
    } catch (error) {
      this.logTwilioEvent('failed-to-initialize-device', { error });
      Metrics.capture({ increment: ['twilio.device.initialize-failure'] });

      if (this.hasActivePhoneNumbers) {
        this.showPhoneUnregisteredError();
      }
    }
  }

  async unregisterDevice() {
    if (this.isInitialized) {
      this.deviceDestroyReason = 'navigation';
      this.windowFocusOnlineAbortController && this.windowFocusOnlineAbortController.abort();
      this.device?.destroy();
      this.isInitialized = false;
    }
  }

  setupWindowListeners() {
    // Clean up any existing listeners to make sure we don't have duplicates
    this.windowFocusOnlineAbortController && this.windowFocusOnlineAbortController.abort();
    this.windowFocusOnlineAbortController = new AbortController();

    let windowFocusOnlineListener = async (eventType: string) => {
      if (!this.isInitialized) {
        return;
      }

      if (!this.device || this.device.state === 'destroyed') {
        this.logTwilioEvent(`${eventType}-listener-setting-up-device`, {});
        await this.setupDevice();
      } else if (this.device.state === 'unregistered') {
        this.logTwilioEvent(`${eventType}-listener-reregistering-device`, {});
        this.attemptDeviceRecovery();
      }
    };

    let signal = this.windowFocusOnlineAbortController.signal;

    window.addEventListener('focus', () => windowFocusOnlineListener('focus'), { signal });
    window.addEventListener('online', () => windowFocusOnlineListener('online'), { signal });
    window.addEventListener('beforeunload', () => (this.deviceDestroyReason = 'navigation'), {
      signal,
    });
  }

  async setupDevice() {
    let tokenData = await this.getToken();
    let options: Device.Options = {
      closeProtection: true,
      enableImprovedSignalingErrorPrecision: true,
      tokenRefreshMs: 60000,
      maxCallSignalingTimeoutMs: 5000,
    };
    this.device = new Device(tokenData.token, options);

    this.device.on('tokenWillExpire', async () => {
      this.logTwilioEvent('tokenWillExpire', {});
      try {
        let newTokenData = await this.getToken();
        if (this.device) {
          this.device.updateToken(newTokenData.token);
        }
      } catch (e: unknown) {
        let errorMessage = e instanceof Error ? e.message : 'Unknown error';
        this.logTwilioEvent('token-refresh-failed-on-expiry', { error: errorMessage });
        Metrics.capture({ increment: ['twilio.device.token_refresh_failure'] });
      }
    });

    this.device.on('incoming', async (call: Call) => await this.handleIncomingCall(call));

    this.device.on(
      'error',
      async (twilioError: TwilioError) => await this.handleDeviceError(twilioError),
    );

    this.device.on('registered', () => {
      this.resetRecoveryState();
      this.deviceDestroyReason = undefined;

      // If we are registering but the service is not initialized, it means we are connecting for the first time.
      let isReconnect = this.isInitialized;

      Metrics.capture({
        increment: ['twilio.device.registered'],
        tags: {
          isReconnect,
        },
      });

      Metrics.capture({ increment: ['twilio.device.count'] });

      this.startSendingDevicePresence();
      this.logTwilioEvent('device-registered', { isReconnect });
      if (this.unregisteredNotification) {
        this.snackbar.clearNotification(this.unregisteredNotification);
        this.unregisteredNotification = undefined;
      }

      if (this.hasActivePhoneNumbers) {
        this.snackbar.notify(this.intl.t('calling.incoming-phone-call-modal.ready-to-receive'), {
          persistent: false,
          clearable: true,
        });
      }
    });

    this.device.on('unregistered', () => this.handleDeviceUnregistered());

    this.device.on('destroyed', () => {
      this.logTwilioEvent('device-destroyed', { reason: this.deviceDestroyReason });

      Metrics.capture({
        increment: ['twilio.device.destroyed'],
        tags: {
          reason: this.deviceDestroyReason,
        },
      });
    });

    await this.device.register();
  }

  addCallListeners(call: Call, direction: 'inbound' | 'outbound') {
    call.on('reconnecting', (twilioError: TwilioError) =>
      this.logTwilioEvent(`${direction}-call-reconnecting`, {}, twilioError),
    );

    call.on('reconnected', () =>
      this.logTwilioEvent(`${direction}-call-reconnected`, call?.parameters),
    );

    call.on('disconnect', (call: Call) =>
      this.logTwilioEvent(`${direction}-call-disconnect`, call.parameters),
    );

    call.on('accept', (call: Call) =>
      this.logTwilioEvent(`${direction}-call-accept`, call.parameters),
    );

    call.on('error', (twilioError: TwilioError) => {
      Metrics.capture({
        increment: ['twilio.call.error'],
        tags: {
          error_code: twilioError.code,
        },
      });

      this.logTwilioEvent(`${direction}-call-error`, {}, twilioError);
    });

    call.on('warning', (warningName: string, warningData) => {
      Metrics.capture({
        increment: ['call.quality.warning'],
        tags: {
          warning_name: warningName,
        },
      });

      this.logTwilioEvent(`${direction}-call-warning`, { warningName, warningData });
    });

    call.on('reject', () => {
      let rejectEventData = {
        conversationId: this.conversation?.id,
        callSid: call.parameters?.CallSid,
        wasShowingRingingUI: this.shouldNotifyTeammate,
        timeSinceIncoming: this.callIncomingTimestamp
          ? Date.now() - this.callIncomingTimestamp
          : null,
      };
      // For inbound calls, a reject event may fire from a server-side "busy" signal
      // without the admin explicitly declining. Dismiss the ringing UI to prevent
      // stale modal states where teammates appear to answer AND decline the same call.
      // See: https://github.com/intercom/intercom/issues/482549
      if (direction === 'inbound') {
        this.incomingCall = null;
        this.shouldNotifyTeammate = false;
        this.resetCallAttributes();
      }
      this.logTwilioEvent(`${direction}-call-reject`, rejectEventData);
      Metrics.capture({ increment: ['twilio.call.reject'], tags: { direction } });
    });

    // Call quality diagnostics - only attach listeners if FF enabled
    if (this.isCallQualityDiagnosticsEnabled()) {
      this.callQualityTracker = new CallQualityTracker({
        appId: this.session.workspace.id,
        adminId: this.session.teammate.id,
        getConnectionType: () => this.getConnectionType(),
        getEdge: () => this.device?.edge ?? undefined,
      });
      this.callQualityTracker.startTracking({
        direction,
        callSid: call.parameters?.CallSid,
        conversationId: this.conversation?.id,
      });

      call.on('accept', () => this.callQualityTracker?.onAccept(call.parameters?.CallSid));
      call.on('sample', (sample: RTCSample) => this.callQualityTracker?.onSample(sample));
      call.on('warning', (warningName: string) => this.callQualityTracker?.onWarning(warningName));
      call.on('error', (error: TwilioError) =>
        this.callQualityTracker?.onError({ code: error.code, message: error.message }),
      );
      call.on('disconnect', () => this.callQualityTracker?.onDisconnect());
    }
  }

  async handleIncomingCall(call: Call) {
    this.callIncomingTimestamp = Date.now();
    this.logTwilioEvent('call-incoming', call.parameters);

    this.adminAwayService.setAdminAsAvailable();
    this.incomingCall = call;

    // Early cancel listener — prevents lost events and stuck modals
    // https://github.com/intercom/intercom/issues/446628
    let callCancelled = false;
    call.on('cancel', () => {
      callCancelled = true;
      let cancelEventData = {
        conversationId: this.conversation?.id,
        callSid: call.parameters?.CallSid,
        wasShowingRingingUI: this.shouldNotifyTeammate,
        timeSinceIncoming: this.callIncomingTimestamp
          ? Date.now() - this.callIncomingTimestamp
          : null,
      };
      this.incomingCall = null;
      this.shouldNotifyTeammate = false;
      this.resetCallAttributes();
      this.logTwilioEvent('call-cancel', cancelEventData);
      Metrics.capture({ increment: ['twilio.call.cancel'] });
    });

    let isManualAnswer = call.customParameters.get('manualAnswer') === 'true';

    if (call.customParameters.get('isTransfer') === 'true') {
      this.incomingCallType = 'transfer';
    } else if (call.customParameters.get('isWarmTransfer') === 'true') {
      this.incomingCallType = 'warm-transfer';
    } else if (call.customParameters.get('isConference') === 'true') {
      this.incomingCallType = 'conference';
    } else {
      this.incomingCallType = 'phone-call';
    }

    Metrics.capture({
      increment: ['twilio.call.incoming'],
      tags: { call_type: this.incomingCallType },
    });

    this.oldAdminId = call.customParameters.get('oldAdminId');
    this.oldTeamId = call.customParameters.get('oldTeamId');
    this.workspacePhoneNumber = `+${call.customParameters.get('workspaceNumber')}`;
    this.calledNumberCountryCode = call.customParameters.get('countryCode');
    this.isListening = call.customParameters.get('listening') === 'true';

    let conversationId = Number(call.customParameters.get('conversationId'));
    this.incomingConversationId = conversationId;

    // Fast modal: show the incoming call notification immediately before loading the
    // full conversation, using caller info from Twilio customParameters.
    // Requires the backend to send callerPhone/callerName in the call parameters.
    // https://github.com/intercom/intercom/issues/434946
    let callerPhone = call.customParameters.get('callerPhone');
    if (!isManualAnswer && callerPhone) {
      this.callerPhone = callerPhone;
      this.callerName = call.customParameters.get('callerName') || undefined;
      this.shouldNotifyTeammate = true;

      if (this.isCallModalDiagnosticsEnabled()) {
        this.sendDiagnosticEvent('incoming_call_received', {
          conversation_id: conversationId,
          call_type: this.incomingCallType,
          tab_hidden: document.hidden,
          fast_modal: true,
          user_is_active: this.lastUserActivityAt
            ? Date.now() - this.lastUserActivityAt < 60000
            : false,
        });
      }

      this.conversationLoadPromise = this.loadConversationInBackground(call, conversationId);
      this.addCallListeners(call, 'inbound');
      return;
    }

    // Original path: load conversation before showing notification
    try {
      this.conversation = await this.inboxApi.fetchConversation(conversationId);

      if (callCancelled) {
        return;
      }

      this.userId = this.conversation.firstParticipant.id;

      let participants = [];

      if (this.isIncomingCallConference) {
        let adminParticipantId = call.customParameters.get('admin_participant');
        let userParticipantId = call.customParameters.get('user_participant');

        participants = await this.loadParticipants(adminParticipantId, userParticipantId);

        if (callCancelled) {
          return;
        }
      } else {
        participants.push(new CallParticipantSummary(this.userSummary!));
        participants.push(new CallParticipantSummary(this.conversation.adminAssignee!));
      }

      this.participants = participants;

      // Fire and forget — analytics should not block the modal
      this.recordEvent('receive_phone_call', this.conversation.id);
    } catch (e: unknown) {
      let errorMessage = e instanceof Error ? e.message : 'Unknown error';
      this.logTwilioEvent('call-incoming-error', { error: errorMessage });
      this.incomingCall = null;
      this.shouldNotifyTeammate = false;
      this.resetCallAttributes();
      return;
    }

    if (callCancelled) {
      return;
    }

    if (isManualAnswer) {
      // The call should only be automatically answered on the tab that pressed manual answer
      // This prevents bad behavior when multiple tabs are open during manual answer
      if (!this.isTabManuallyAnswering) {
        return;
      }
      this.shouldNotifyTeammate = false;
      await this.acceptCall();
      // Re-enable the incoming ringer for the next call
      this.device?.audio?.incoming(true);
      this.isTabManuallyAnswering = false;
    } else {
      this.shouldNotifyTeammate = true;

      if (this.isCallModalDiagnosticsEnabled()) {
        this.sendDiagnosticEvent('incoming_call_received', {
          conversation_id: this.conversation?.id,
          call_type: this.incomingCallType,
          tab_hidden: document.hidden,
          fast_modal: false,
          user_is_active: this.lastUserActivityAt
            ? Date.now() - this.lastUserActivityAt < 60000
            : false,
        });
      }
    }

    this.addCallListeners(call, 'inbound');
  }

  private async loadConversationInBackground(call: Call, conversationId: number): Promise<void> {
    try {
      this.conversation = await this.inboxApi.fetchConversation(conversationId);
      this.userId = this.conversation.firstParticipant.id;

      let participants = [];

      if (this.isIncomingCallConference) {
        let adminParticipantId = call.customParameters.get('admin_participant');
        let userParticipantId = call.customParameters.get('user_participant');
        participants = await this.loadParticipants(adminParticipantId, userParticipantId);
      } else {
        participants.push(new CallParticipantSummary(this.userSummary!));
        participants.push(new CallParticipantSummary(this.conversation.adminAssignee!));
      }

      this.participants = participants;

      // Fire and forget — analytics should not block the call
      this.recordEvent('receive_phone_call', this.conversation.id);
    } catch (e: unknown) {
      let errorMessage = e instanceof Error ? e.message : 'Unknown error';
      this.logTwilioEvent('call-incoming-error', { error: errorMessage });
      this.incomingCall = null;
      this.shouldNotifyTeammate = false;
      this.resetCallAttributes();
    }
  }

  async handleDeviceUnregistered() {
    this.stopSendingDevicePresence();
    this.logTwilioEvent('device-unregistered', {});

    Metrics.capture({ increment: ['twilio.device.unregistered'] });
    Metrics.capture({ decrement: ['twilio.device.count'] });

    let recovering = await this.attemptDeviceRecovery();
    if (recovering) {
      return;
    }

    if (!this.hasActivePhoneNumbers) {
      return;
    }
    // If we are in the inbox, show the unregistered toast so they know their phone is broken,
    // otherwise we have left the inbox, so we should show the warning that they won't be getting any calls.
    if (this.isPhoneRoute) {
      this.showPhoneUnregisteredError();
    } else if (localStorage.getItem(PHONE_HELP_DESK_ONLY_NOTIFICATION_DISMISSED) !== 'true') {
      // Teammate has moved away from the help desk
      this.notificationsService.notifyWarning(
        this.intl.t('calling.incoming-phone-call-modal.unregistered-away-from-help-desk', {
          url: '#',
          htmlSafe: true,
        }),
        NOTIFICATION_TIMEOUT,
      );
      localStorage.setItem(PHONE_HELP_DESK_ONLY_NOTIFICATION_DISMISSED, 'true');
    }
  }

  private async attemptDeviceRecovery(): Promise<boolean> {
    if (this.isRecovering) {
      return true;
    }
    if (this.recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
      return false;
    }

    this.isRecovering = true;
    let attempt = this.recoveryAttempts;
    let delay = Math.min(INITIAL_RECOVERY_DELAY_MS * Math.pow(2, attempt), MAX_RECOVERY_DELAY_MS);

    this.logTwilioEvent('recovery-attempt', { attempt, delay, maxAttempts: MAX_RECOVERY_ATTEMPTS });

    if (this.isDiagnosticsEnabled()) {
      this.sendDiagnosticEvent('recovery_attempt', { attempt, delay });
    }

    this.recoveryTimer = setTimeout(async () => {
      try {
        if (this.device && this.device.state !== 'destroyed') {
          await this.device.register();
        } else if (this.isInitialized) {
          await this.setupDevice();
        }
        // Success handled by 'registered' event listener which calls resetRecoveryState()
      } catch (e: unknown) {
        let errorMessage = e instanceof Error ? e.message : 'Unknown error';
        this.logTwilioEvent('recovery-failed', { attempt, error: errorMessage });
        this.recoveryAttempts++;
        this.isRecovering = false;
        if (this.recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
          this.attemptDeviceRecovery();
        } else {
          this.logTwilioEvent('recovery-exhausted', { totalAttempts: this.recoveryAttempts });
        }
      }
    }, delay);

    return true;
  }

  private resetRecoveryState() {
    this.recoveryAttempts = 0;
    this.isRecovering = false;
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = undefined;
    }
  }

  async handleDeviceError(twilioError: TwilioError) {
    this.logTwilioEvent('device-error', {}, twilioError);

    Metrics.capture({
      increment: ['twilio.device.error'],
      tags: {
        error_code: twilioError.code,
      },
    });

    if (twilioError.code === 20104 && this.device) {
      try {
        let newTokenData = await this.getToken();
        this.device.updateToken(newTokenData.token);
      } catch (e: unknown) {
        let errorMessage = e instanceof Error ? e.message : 'Unknown error';
        this.logTwilioEvent('token-refresh-failed-on-device-error', { error: errorMessage });
        Metrics.capture({ increment: ['twilio.device.token_refresh_failure'] });
      }
    }
  }

  showPhoneUnregisteredError() {
    if (!this.unregisteredNotification) {
      this.unregisteredNotification = this.snackbar.notify(
        this.intl.t('calling.incoming-phone-call-modal.unregistered'),
        {
          type: 'error',
          persistent: true,
          clearable: true,
          contentComponent: 'inbox2/left-nav/notification-nexus-error',
        },
      );
    }
  }

  async loadParticipants(
    adminParticipantId?: string,
    userParticipantId?: string,
  ): Promise<CallParticipantSummary[]> {
    if (!this.conversation) {
      return [];
    }

    let participants: CallParticipantSummary[] = [];

    if (adminParticipantId) {
      let foundAdmin = this.inbox2AssigneeSearch.findAdminById(Number(adminParticipantId));

      if (foundAdmin) {
        participants.push(new CallParticipantSummary(foundAdmin));
      }
    }

    if (userParticipantId) {
      // Lets try and load from the conversation first before doing an API call
      let foundUser: ParticipantUserSummary | null | undefined =
        this.conversation.participantSummaries.findBy('id', userParticipantId);

      if (foundUser) {
        participants.push(new CallParticipantSummary(foundUser));
      } else {
        foundUser = await this.inboxSearchSuggestionsService.loadUserFromId(userParticipantId);

        if (foundUser) {
          participants.push(new CallParticipantSummary(foundUser));
        }
      }
    }

    return participants;
  }

  get oldAdmin() {
    if (this.oldAdminId) {
      return this.inbox2AssigneeSearch.findAdminById(Number(this.oldAdminId));
    }

    return undefined;
  }

  async handleConferenceParticipantAdded(event: ConferenceParticipantAddedEvent) {
    if (!this.conversation || this.conversation.id !== event.eventData.conversationId) {
      return;
    }

    if (!this.isActiveCall) {
      return;
    }

    await this.updateParticipants(event.eventData);
    this.recordEvent('conference_participant_added', this.conversation.id);

    if (!this.addingParticipantTimer) {
      return;
    }

    if (!this.addingCallParticipantInfo) {
      return;
    }

    let participantAdded = this.participants.find((p) => {
      if (p.id === this.addingCallParticipantInfo?.identifier) {
        return true;
      }

      return p.phone && p.phone === this.addingCallParticipantInfo?.identifier;
    });

    if (participantAdded) {
      this.tearDownAddingParticipantTimer();
    }
  }

  async handleConferenceParticipantRemoved(event: ConferenceParticipantRemovedEvent) {
    if (!this.conversation || this.conversation.id !== event.eventData.conversationId) {
      return;
    }

    if (!this.isActiveCall) {
      return;
    }

    await this.updateParticipants(event.eventData);
    this.recordEvent('conference_participant_removed', this.conversation.id);
  }

  async handleConferenceParticipantFailedToJoin(event: ConferenceParticipantFailedToJoinEvent) {
    if (!this.conversation || this.conversation.id !== event.eventData.conversationId) {
      return;
    }

    if (!this.isActiveCall) {
      return;
    }

    await this.updateParticipants(event.eventData);
    this.recordEvent('conference_participant_failed_to_join', this.conversation.id);

    if (!this.addingParticipantTimer) {
      return;
    }

    this.tearDownAddingParticipantTimer();
  }

  async handleWarmTransferAdded(event: WarmTransferAddedEvent) {
    this.warmTransferState = 'talking';
    this.warmTransferStartTime = moment().valueOf();

    this.warmTransferToTeamCallingInfo = undefined;
    this.warmTransferToTeamStatus = undefined;

    if (this.warmTransferTeamId !== undefined) {
      let admin = this.inbox2AssigneeSearch.findAdminById(event.eventData.answeredByAdminId);
      this.addingCallParticipantInfo = {
        identifier: event.eventData.answeredByAdminId,
        label: admin?.name ?? '',
      };
    }
  }

  async handleWarmTransferCompleted(event: WarmTransferCompletedEvent) {
    let admin = this.inbox2AssigneeSearch.findAdminById(event.eventData.adminId);
    this.notifyTransferSuccess(admin?.name);
    if (this.isIncomingCallWarmTransfer) {
      this.incomingCallType = 'phone-call';
      this.activeCall?.handleAccepted();
    }
  }

  async handleWarmTransferFailed(event: WarmTransferFailedEvent) {
    if (!this.isWarmTransfer) {
      return;
    }

    this.notifyTransferFailure(event.eventData.status, this.addingCallParticipantInfo?.label);
    this.currentCallAction = undefined;
    this.addingCallParticipantInfo = undefined;
    this.resetWarmTransferState();
    this.isOnHold = true;
  }

  handleWarmTransferToTeamWaiting() {
    // If we're showing a declined/no-answer status, delay the transition to waiting
    // so the user can see who declined/didn't answer before moving to the next retry
    if (this.warmTransferToTeamStatus) {
      this.clearWarmTransferToTeamWaitingTimer();
      this.warmTransferToTeamWaitingTimer = setTimeout(() => {
        this.warmTransferToTeamWaiting = true;
        this.warmTransferToTeamStatus = undefined;
        this.warmTransferToTeamCallingInfo = undefined;
      }, 2000);
    } else {
      this.warmTransferToTeamWaiting = true;
      this.warmTransferToTeamStatus = undefined;
      this.warmTransferToTeamCallingInfo = undefined;
    }
  }

  private clearWarmTransferToTeamWaitingTimer() {
    if (this.warmTransferToTeamWaitingTimer) {
      clearTimeout(this.warmTransferToTeamWaitingTimer);
      this.warmTransferToTeamWaitingTimer = null;
    }
  }

  private resetWarmTransferState() {
    this.warmTransferState = undefined;
    this.warmTransferStartTime = undefined;
    this.warmTransferUser = undefined;
    this.warmTransferTeamId = undefined;
    this.warmTransferToTeamWaiting = false;
    this.warmTransferToTeamCallingInfo = undefined;
    this.warmTransferToTeamStatus = undefined;
    this.clearWarmTransferToTeamWaitingTimer();
  }

  handleWarmTransferToTeamCalling(event: WarmTransferToTeamCallingEvent) {
    this.clearWarmTransferToTeamWaitingTimer();
    this.warmTransferToTeamWaiting = false;
    this.warmTransferToTeamStatus = undefined;
    this.warmTransferToTeamCallingInfo = {
      adminId: event.eventData.newAdminId,
      startedAt: event.eventData.callingStartedAt,
      timeoutSeconds: event.eventData.timeoutSeconds,
    };
  }

  get warmTransferToTeamCallingAdmin(): AdminSummary | undefined {
    if (this.warmTransferToTeamCallingInfo === undefined) {
      return undefined;
    }
    return this.inbox2AssigneeSearch.findAdminById(this.warmTransferToTeamCallingInfo.adminId);
  }

  handleWarmTransferToTeamFailed(event: WarmTransferToTeamFailedEvent) {
    let { callStatus, newAdminId } = event.eventData;

    switch (callStatus) {
      case 'busy':
        this.warmTransferToTeamCallingInfo = undefined;
        this.warmTransferToTeamStatus = { type: 'declined', adminId: newAdminId };
        break;
      case 'no-answer':
        this.warmTransferToTeamCallingInfo = undefined;
        this.warmTransferToTeamStatus = { type: 'no-answer', adminId: newAdminId };
        break;
    }
  }

  get warmTransferToTeamStatusAdmin(): AdminSummary | undefined {
    if (this.warmTransferToTeamStatus === undefined) {
      return undefined;
    }
    return this.inbox2AssigneeSearch.findAdminById(this.warmTransferToTeamStatus.adminId);
  }

  async updateParticipants(
    eventData:
      | ConferenceParticipantAddedEvent['eventData']
      | ConferenceParticipantRemovedEvent['eventData']
      | ConferenceParticipantFailedToJoinEvent['eventData'],
  ) {
    let participants: CallParticipantSummary[] = [];
    let userPromises: Promise<UserSummary | null>[] = [];

    eventData.userParticipants.forEach((id) => {
      let foundUser = this.participants.find((p) => p.id === id);

      if (foundUser) {
        participants.push(foundUser);
        return;
      }

      userPromises.push(this.inboxSearchSuggestionsService.loadUserFromId(id));
    });

    eventData.adminParticipants.forEach((id) => {
      let foundAdmin = this.participants.find((p) => p.id === id);

      if (foundAdmin) {
        participants.push(foundAdmin);
        return;
      }

      let loadedAdmin = this.inbox2AssigneeSearch.findAdminById(id);
      if (loadedAdmin) {
        participants.push(new CallParticipantSummary(loadedAdmin));
      }
    });

    let loadedUsers = await Promise.all(userPromises);

    loadedUsers.forEach((user) => {
      if (user) {
        participants.push(new CallParticipantSummary(user));
      }
    });

    // Handle hold state transitions between 1-on-1 and conference modes

    // If transitioning from 1-on-1 (where service-level hold was used) to conference,
    // transfer the hold state to user participants
    if (this.isOnHold && this.participants.length < 3) {
      let userParticipants = participants.filter((p) => p.isUser);

      // Mark only the user(s) who were in the previous call as on hold
      userParticipants.forEach((participant) => {
        let wasInPreviousCall = this.participants.find((p) => p.id === participant.id);
        if (wasInPreviousCall) {
          participant.isOnHold = true;
        }
      });
    }

    // If transitioning from conference to 1-on-1, sync participant-level hold to service-level hold
    if (this.participants.length >= 3 && participants.length < 3) {
      let userParticipants = participants.filter((p) => p.isUser);
      let anyUserOnHold = userParticipants.some((p) => p.isOnHold);

      // Explicitly set to the value to ensure it's cleared when no one is on hold
      this.isOnHold = anyUserOnHold;

      // Update userId to point to the remaining user participant
      // This ensures the correct user is displayed in the 1-on-1 UI
      if (userParticipants.length > 0) {
        this.userId = String(userParticipants[0].id);
      } else {
        // For admin-only calls with individual hold controls, keep existing userId
        // to maintain proper UI state in conference mode
      }
    }

    this.participants = participants;
  }

  transitionFromListeningToParticipant() {
    let participants: CallParticipantSummary[] = [];

    if (this.userSummary) {
      participants.push(new CallParticipantSummary(this.userSummary));
    }

    if (this.conversation?.adminAssignee) {
      participants.push(new CallParticipantSummary(this.conversation.adminAssignee));
    }

    // Add the current admin (barging admin) as a participant
    participants.push(new CallParticipantSummary(this.session.teammate));

    this.participants = participants;
    this.isListening = false;
    this.intercomCallService.setAdminOnCall();
  }

  async acceptListeningCall(conversationId?: number | undefined) {
    this.shouldNotifyTeammate = false;
    if (conversationId && !this.conversation) {
      this.conversation = await this.inboxApi.fetchConversation(conversationId);
    }
    if (!this.conversation || !this.incomingCall) {
      return;
    }
    this.incomingCall.accept();
    this.intercomCallService.setAdminAsCoaching();
    this.activeCall = new PhoneCall(this.incomingCall, this.conversation);
    this.dispatchCallingStateToReact();
    this.recordEvent('start_listening_to_call', this.conversation.id);

    this.activeCall.onStateChange(async (event: CustomEvent) => {
      if (this.activeCall) {
        this.activeCall.callState = event.detail.type;
      }

      if (event.detail.type === 'disconnect') {
        if (this.conversation) {
          this.recordEvent('end_listening_to_call', this.conversation.id);
        }

        this.activeCall = undefined;
        this.dispatchCallingStateToReact();
        this.incomingCall = null;
        if (this.session.workspace?.isFeatureEnabled('fix-phone-call-listening')) {
          this.conversation = undefined;
        }

        this.isListening = false;
        this.shouldNotifyTeammate = false;
      }
    });
  }

  async acceptCall() {
    // Fast modal: conversation may still be loading in background — wait for it.
    // Keep shouldNotifyTeammate true until conversation is ready so the alert
    // stays visible (the component shows a "Connecting..." state on the button).
    if (this.conversationLoadPromise) {
      await this.conversationLoadPromise;
    }

    this.shouldNotifyTeammate = false;

    if (!this.conversation || !this.incomingCall) {
      this.logTwilioEvent('accept-call-stale', {
        hasConversation: !!this.conversation,
        hasIncomingCall: !!this.incomingCall,
        timeSinceIncoming: this.callIncomingTimestamp
          ? Date.now() - this.callIncomingTimestamp
          : null,
      });
      Metrics.capture({ increment: ['twilio.call.accept_stale'] });
      this.callIncomingTimestamp = undefined;
      return;
    }

    if (this.isIncomingCallTransfer) {
      await this.removeAdminFromCall();
    } else if (this.isIncomingCallConference) {
      this.participants = [...this.participants, new CallParticipantSummary(this.session.teammate)];
    }

    this.incomingCall.accept();
    this.intercomCallService.setAdminOnCall();
    this.activeCall = new PhoneCall(this.incomingCall, this.conversation);
    this.dispatchCallingStateToReact();
    this.recordEvent('accept_inbound_phone_call', this.conversation.id);
    Metrics.capture({ increment: ['twilio.call.accept'] });
    this.callIncomingTimestamp = undefined;

    this.activeCall.onStateChange(async (event: CustomEvent) => {
      if (this.activeCall) {
        this.activeCall.callState = event.detail.type;
      }
      let callConversation = this.activeCall?.conversation;
      let hangedUpByAdmin = this.activeCall?.hangedUpByAdmin;

      if (event.detail.type === 'disconnect') {
        let duration = this.activeCall?.duration;
        let isTrialTermination = this.isTrialCallTermination(duration);
        if (this.conversation) {
          this.recordEvent('end_inbound_phone_call', this.conversation.id, duration);
        }

        if (this.isColdTransfer && !hangedUpByAdmin) {
          this.notifyTransferSuccess();
        }

        this.incomingCall = null;
        this.resetCallAttributes();

        if (isTrialTermination) {
          this.showTrialCallTerminationNotification();
        }

        if (event.detail.oldType === 'accept' && callConversation instanceof Conversation) {
          await this.intercomCallService.setEndCallAdminState();
        }
      }
    });

    this.navigateToConversation();
  }

  get isColdTransfer() {
    return this.isTransferToTeammate || this.isTransferToExternalNumber;
  }

  async rejectCall() {
    if (!this.incomingCall) {
      return;
    }

    this.shouldNotifyTeammate = false;
    this.incomingCall.reject();
    this.resetCallAttributes();
  }

  async callStoredUser(
    conversation: Conversation,
    user: User | UserSummary,
    workspaceNumber: string | null = null,
  ) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    this.intercomCallService.setAdminOnCall();

    this.isRecordingEnabled = this.isRecordingEnabledOutbound;
    this.isRecording = this.isRecordingEnabled;

    this.conversation = conversation;
    this.userId = user.id.toString();

    // No phone number passed — backend resolves it from UserId
    let outgoingCallParams: any = {
      AppId: this.session.workspace.id,
      ConversationId: conversation.id.toString(),
      AdminId: this.session.teammate.id.toString(),
      UserId: this.userId,
      WorkspacePhoneNumber: workspaceNumber,
    };

    if (this.intercomCallService.nextCallIsTestCall) {
      this.intercomCallService.nextCallIsTestCall = false;
      outgoingCallParams.IsFreeUsageTestCall = true;
    }

    let outgoingCall = await this.device?.connect({
      params: outgoingCallParams,
    });

    return this.setupOutgoingCall(outgoingCall, conversation, workspaceNumber);
  }

  async callNumber(
    phoneNumber: string,
    conversation: Conversation,
    user: User | UserSummary,
    workspaceNumber: string | null = null,
  ) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Validate phone number format
    if (!isValidE164PhoneNumber(phoneNumber)) {
      this.snackbar.notify(
        this.intl.t('calling.invalid-phone-number-format', {
          phoneNumber,
        }),
        { type: 'error' },
      );
      return null;
    }

    this.intercomCallService.setAdminOnCall();

    this.isRecordingEnabled = this.isRecordingEnabledOutbound;
    this.isRecording = this.isRecordingEnabled;

    this.conversation = conversation;
    this.userId = user.id.toString();

    let outgoingCallParams: any = {
      To: phoneNumber,
      AppId: this.session.workspace.id,
      ConversationId: conversation.id.toString(),
      AdminId: this.session.teammate.id.toString(),
      UserId: this.userId,
    };

    outgoingCallParams = {
      ...outgoingCallParams,
      WorkspacePhoneNumber: workspaceNumber,
    };

    if (this.intercomCallService.nextCallIsTestCall) {
      this.intercomCallService.nextCallIsTestCall = false;
      outgoingCallParams.IsFreeUsageTestCall = true;
    }

    let outgoingCall = await this.device?.connect({
      params: outgoingCallParams,
    });

    return this.setupOutgoingCall(outgoingCall, conversation, workspaceNumber);
  }

  private setupOutgoingCall(
    outgoingCall: Call | undefined,
    conversation: Conversation,
    workspaceNumber: string | null,
  ) {
    if (!(outgoingCall instanceof Call)) {
      return null;
    }

    this.activeCall = new PhoneCall(outgoingCall, conversation);
    this.dispatchCallingStateToReact();
    this.recordEvent('initiate_phone_call', conversation.id);

    this.participants = [
      new CallParticipantSummary(this.userSummary!),
      new CallParticipantSummary(this.session.teammate),
    ];

    this.workspacePhoneNumber = workspaceNumber || undefined;

    this.activeCall.onStateChange(async (event: CustomEvent) => {
      if (this.activeCall) {
        this.activeCall.callState = event.detail.type;
      }
      let callConversation = this.activeCall?.conversation;

      if (event.detail.type === 'disconnect') {
        let duration = this.activeCall?.duration;
        let hangedUpByAdmin = this.activeCall?.hangedUpByAdmin;
        let isTrialTermination = this.isTrialCallTermination(duration);
        this.recordEvent('end_phone_call', conversation.id, duration);

        if (this.isColdTransfer && !hangedUpByAdmin) {
          this.notifyTransferSuccess();
        }

        this.resetCallAttributes();

        if (isTrialTermination) {
          this.showTrialCallTerminationNotification();
        }

        if (event.detail.oldType === 'accept' && callConversation instanceof Conversation) {
          await this.intercomCallService.setEndCallAdminState();
        }
        if (
          ['ringing', 'error'].includes(event.detail.oldType) &&
          callConversation instanceof Conversation
        ) {
          if (hangedUpByAdmin) {
            this.adminAwayService.setAdminAsAvailable();
          } else {
            await this.intercomCallService.endCall(callConversation.id, duration);
          }
        }
      } else if (event.detail.type === 'accept' && callConversation instanceof Conversation) {
        await this.intercomCallService.startEscalationCall(
          callConversation.id,
          outgoingCall?.parameters.CallSid,
        );

        this.recordEvent('phone_call_accepted', conversation.id);
      }
    });

    this.addCallListeners(outgoingCall, 'outbound');

    return this.activeCall;
  }

  notifyTransferSuccess(transferee = this.addingCallParticipantInfo?.label) {
    this.snackbar.notify(
      this.intl.t('calling.incoming-phone-call-modal.successful-transfer', {
        name: transferee,
      }),
      { timeout: WARM_TRANSFER_NOTIFICATION_TIMEOUT },
    );
  }

  notifyTransferFailure(status: string, transferee?: string) {
    this.snackbar.notify(
      this.intl.t(`calling.incoming-phone-call-modal.failed-transfer-${status}`, {
        name: transferee,
      }),
      { type: 'error', timeout: WARM_TRANSFER_NOTIFICATION_TIMEOUT },
    );
  }

  private getTransferErrorMessage(error: any): string {
    let backendErrorKey = error.jqXHR?.responseJSON?.error;

    if (backendErrorKey && typeof backendErrorKey === 'string') {
      return this.intl.t(`calling.errors.${backendErrorKey}`);
    }

    // Fallback for 409 errors without a recognized error key
    if (error.jqXHR?.status === 409) {
      return this.intl.t('calling.errors.admin_unavailable');
    }

    return this.intl.t('calling.errors.calling-transfer-failure');
  }

  private buildHoldPayload(participant: CallParticipantSummary) {
    let payload: {
      app_id: string;
      conversation_id: number;
      admin_id?: number | string;
      user_id?: string;
      external_number?: string;
    } = {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation!.id,
    };

    if (participant.isAdmin) {
      payload.admin_id = participant.id;
    } else if (participant.id === this.userId) {
      payload.user_id = participant.id;
    } else {
      payload.external_number = participant.phone;
    }

    return payload;
  }

  @task({ drop: true })
  *toggleOnHold(): TaskGenerator<void> {
    if (this.disableHold) {
      return;
    }

    if (!this.conversation) {
      return;
    }

    let url = this.isOnHold ? '/ember/phone_call/take_off_hold' : '/ember/phone_call/place_on_hold';

    let userParticipant = this.participants.find((p) => !p.isAdmin);
    if (!userParticipant) {
      return;
    }

    yield post(url, this.buildHoldPayload(userParticipant));
    this.isOnHold = !this.isOnHold;
  }

  @task({ drop: true })
  *toggleTransferOnHold(): TaskGenerator<void> {
    if (!this.conversation) {
      return;
    }

    let url = this.isOnHold
      ? '/ember/phone_call/take_transfer_off_hold'
      : '/ember/phone_call/place_transfer_on_hold';
    yield post(url, {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation.id,
      admin_id: this.addingCallParticipantInfo?.identifier,
      external_number: this.warmTransferUser?.phone,
    });
    this.isOnHold = !this.isOnHold;
  }

  @task({ drop: true })
  *toggleParticipantHold(participant: CallParticipantSummary): TaskGenerator<void> {
    if (!this.conversation) {
      return;
    }

    let url = participant.isOnHold
      ? '/ember/phone_call/take_off_hold'
      : '/ember/phone_call/place_on_hold';

    yield post(url, this.buildHoldPayload(participant));

    participant.isOnHold = !participant.isOnHold;
  }

  @task
  *toggleRecording() {
    if (!this.conversation) {
      return;
    }

    if (this.isRecording) {
      yield this.intercomCallService.stopRecording(this.conversation.id);
      this.isRecording = false;
    } else {
      yield this.intercomCallService.startRecording(this.conversation.id);
      this.isRecording = true;
    }
  }

  @task({ drop: true })
  *transferToTeam(teamId: number): TaskGenerator<void> {
    if (!this.conversation || !this.incomingCall || !this.isActiveCall) {
      return;
    }

    yield post('/ember/phone_call/transfer_to_team', {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation.id,
      team_id: teamId,
    });
  }

  @task({ drop: true })
  *transferToAdmin(admin: AdminSummary): TaskGenerator<void> {
    if (!this.conversation || !this.isActiveCall) {
      return;
    }
    try {
      this.currentCallAction = 'transferring-to-teammate';
      this.addingCallParticipantInfo = { identifier: admin.id, label: admin.name };
      this.isOnHold = true;
      this.disableHold = true;
      yield post('/ember/phone_call/transfer_to_admin', {
        app_id: this.session.workspace.id,
        conversation_id: this.conversation.id,
        admin_id: admin.id,
      });
      // TODO: See if there isn't a better way to handle this
      yield timeout(10000);
      this.disableHold = false;
    } catch (e) {
      this.currentCallAction = undefined;
      this.addingCallParticipantInfo = undefined;
      this.disableHold = false;
      this.isOnHold = false;

      let errorMessage = this.getTransferErrorMessage(e);
      this.notificationsService.notifyError(errorMessage);
    }
  }

  @task({ drop: true })
  *warmTransferToAdmin(admin: AdminSummary): TaskGenerator<void> {
    if (!this.conversation || !this.isActiveCall) {
      return;
    }

    try {
      this.currentCallAction = 'warm-transferring-to-teammate';
      this.warmTransferState = 'ringing';
      this.addingCallParticipantInfo = { identifier: admin.id, label: admin.name };
      this.isOnHold = false;
      yield post('/ember/phone_call/start_warm_transfer_to_admin', {
        app_id: this.session.workspace.id,
        conversation_id: this.conversation.id,
        admin_id: admin.id,
      });
    } catch (e) {
      this.currentCallAction = undefined;
      this.warmTransferState = undefined;
      this.addingCallParticipantInfo = undefined;

      let errorMessage = this.getTransferErrorMessage(e);
      this.notificationsService.notifyError(errorMessage);
    }
  }

  @task({ drop: true })
  *warmTransferToTeam(team: TeamSummary): TaskGenerator<void> {
    if (!this.conversation || !this.isActiveCall) {
      return;
    }

    try {
      this.currentCallAction = 'warm-transferring-to-teammate';
      this.warmTransferState = 'ringing';
      this.warmTransferTeamId = team.id;
      this.addingCallParticipantInfo = { identifier: team.id, label: team.name };
      this.isOnHold = false;
      yield post('/ember/phone_call/start_warm_transfer_to_team', {
        app_id: this.session.workspace.id,
        conversation_id: this.conversation.id,
        team_id: team.id,
      });
    } catch (e) {
      this.currentCallAction = undefined;
      this.warmTransferState = undefined;
      this.warmTransferTeamId = undefined;
      this.addingCallParticipantInfo = undefined;
      this.notificationsService.notifyError(this.intl.t('calling.errors.calling-transfer-failure'));
    }
  }

  @task({ drop: true })
  *transferToExternalNumber(externalNumber: string): TaskGenerator<void> {
    this.addingCallParticipantInfo = { identifier: externalNumber, label: externalNumber };

    if (!this.conversation || !this.isActiveCall) {
      return;
    }

    try {
      this.currentCallAction = 'transferring-externally';
      this.isOnHold = true;
      this.disableHold = true;
      yield post('/ember/phone_call/transfer_to_external_number', {
        app_id: this.session.workspace.id,
        conversation_id: this.conversation.id,
        external_number: externalNumber,
      });
      if (!Ember.testing) {
        yield timeout(10000);
      }
      this.disableHold = false;
    } catch (e) {
      this.currentCallAction = undefined;
      this.disableHold = false;
      this.isOnHold = false;
      this.notificationsService.notifyError(this.intl.t('calling.errors.calling-transfer-failure'));
    }
  }

  @task({ drop: true })
  *warmTransferToExternalNumber(externalNumber: string) {
    if (!this.conversation || !this.isActiveCall) {
      return;
    }

    try {
      this.currentCallAction = 'warm-transferring-externally';
      this.warmTransferState = 'ringing';
      let { id } = yield this.intercomCallService.findOrCreateContact(externalNumber);
      this.warmTransferUser =
        (yield this.inboxSearchSuggestionsService.loadUserFromId(id)) || undefined;
      this.addingCallParticipantInfo = {
        identifier: id,
        label: this.warmTransferUser?.name || externalNumber,
      };
      this.isOnHold = false;
      yield post('/ember/phone_call/start_warm_transfer_to_external_number', {
        app_id: this.session.workspace.id,
        conversation_id: this.conversation.id,
        external_number: externalNumber,
      });
    } catch (e) {
      this.currentCallAction = undefined;
      this.warmTransferState = undefined;
      this.addingCallParticipantInfo = undefined;
      this.warmTransferUser = undefined;
      this.notificationsService.notifyError(this.intl.t('calling.errors.calling-transfer-failure'));
    }
  }

  @task({ drop: true })
  *transferToWorkflow(workflowId: number, workflowName: string): TaskGenerator<void> {
    if (!this.conversation || !this.isActiveCall) {
      return;
    }

    try {
      this.currentCallAction = 'transferring-to-workflow';
      this.addingCallParticipantInfo = { identifier: workflowId, label: workflowName };
      this.isOnHold = true;
      this.disableHold = true;
      yield post('/ember/phone_call/transfer_to_workflow', {
        app_id: this.session.workspace.id,
        conversation_id: this.conversation.id,
        reusable_workflow_id: workflowId,
      });
      yield timeout(10000);
      this.disableHold = false;
    } catch (e) {
      this.currentCallAction = undefined;
      this.addingCallParticipantInfo = undefined;
      this.disableHold = false;
      this.isOnHold = false;

      if (e.jqXHR?.status === 422 && e.jqXHR?.responseJSON?.errors) {
        let errorMessage =
          e.jqXHR.responseJSON.errors[0]?.message ||
          this.intl.t('calling.errors.calling-transfer-failure');
        this.notificationsService.notifyError(errorMessage);
      } else {
        this.notificationsService.notifyError(
          this.intl.t('calling.errors.calling-transfer-failure'),
        );
      }
    }
  }

  @task({ drop: true })
  *addTeammateToCall(participant: AdminSummary) {
    if (!this.conversation || !this.isActiveCall) {
      return;
    }

    try {
      this.addingCallParticipantInfo = { identifier: participant.id, label: participant.name };
      this.currentCallAction = 'adding-participant';

      yield post('/ember/conference_calls/add_admin_participant', {
        app_id: this.session.workspace.id,
        conversation_id: this.conversation.id,
        admin_id: participant.id,
      });

      this.setupAddingParticipantTimer();
      this.recordEvent('add_teammate_to_call', this.conversation.id);
    } catch (e) {
      this.currentCallAction = undefined;
      this.addingCallParticipantInfo = undefined;

      if (e.jqXHR.status === 400) {
        this.notificationsService.notifyError(
          this.intl.t('calling.errors.calling-transfer-admin-unavailable'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('calling.errors.calling-add-to-conference-failure'),
        );
      }
    }
  }

  @task({ drop: true })
  *addExternalParticipantToCall(externalNumber: string): TaskGenerator<void> {
    if (!this.conversation || !this.isActiveCall) {
      return;
    }

    try {
      this.addingCallParticipantInfo = { identifier: externalNumber, label: externalNumber };
      this.currentCallAction = 'adding-participant';

      yield post('/ember/conference_calls/add_external_number_participant', {
        app_id: this.session.workspace.id,
        conversation_id: this.conversation.id,
        phone_number: externalNumber,
      });

      this.setupAddingParticipantTimer();
      this.recordEvent('add_external_participant_to_call', this.conversation.id);
    } catch (e) {
      this.currentCallAction = undefined;
      this.addingCallParticipantInfo = undefined;

      this.notificationsService.notifyError(
        this.intl.t('calling.errors.calling-add-to-conference-failure'),
      );
    }
  }

  async notifyInboundCallback(event: InboundCallbackRequestEvent) {
    this.callbackAudio.loop = true;
    this.callbackAudio.play();
    this.incomingCallType = 'callback';
    this.acceptedCallback = false;
    this.callbackAdminId = event.eventData.adminId;
    this.calledNumberCountryCode = event.eventData.countryCode;
    this.resolvedOutboundNumber = event.eventData.resolvedOutboundNumber;
    this.conversation = await this.inboxApi.fetchConversation(event.eventData.conversationId);
    this.userId = this.conversation.firstParticipant.id;
    this.shouldNotifyTeammate = true;

    if (this.isCallModalDiagnosticsEnabled()) {
      this.sendDiagnosticEvent('incoming_call_received', {
        conversation_id: this.conversation?.id,
        call_type: 'callback',
        tab_hidden: document.hidden,
        user_is_active: this.lastUserActivityAt
          ? Date.now() - this.lastUserActivityAt < 60000
          : false,
      });
    }

    this.callbackAcceptanceTimer = setTimeout(() => {
      if (!this.acceptedCallback) {
        this.ignoreCallback();
        this.conversation = undefined;
        this.userId = undefined;
      }
    }, NOTIFICATION_TIMEOUT);
  }

  async teammateHangUp() {
    if (!this.conversation || !this.incomingCall) {
      return;
    }

    await post('/ember/phone_call/teammate_hangup', {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation.id,
      is_warm_transfer: this.isIncomingCallWarmTransfer,
      inviting_admin_id: this.oldAdminId,
    });
  }

  private getWarmTransferUrlAndParams(action: 'cancel' | 'complete') {
    let baseParams = {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation!.id,
    };

    if (this.isWarmTransferToExternalNumber) {
      return {
        url: `/ember/phone_call/${action}_warm_transfer_to_external_number`,
        params: { ...baseParams, external_number: this.warmTransferUser?.phone },
      };
    }

    if (this.warmTransferTeamId !== undefined) {
      // Cancel uses team endpoint, complete uses admin endpoint with answering admin
      if (action === 'cancel') {
        return {
          url: `/ember/phone_call/cancel_warm_transfer_to_team`,
          params: { ...baseParams, team_id: this.warmTransferTeamId },
        };
      } else {
        return {
          url: `/ember/phone_call/complete_warm_transfer_to_admin`,
          params: { ...baseParams, admin_id: this.addingCallParticipantInfo?.identifier },
        };
      }
    }

    return {
      url: `/ember/phone_call/${action}_warm_transfer_to_admin`,
      params: { ...baseParams, admin_id: this.addingCallParticipantInfo?.identifier },
    };
  }

  async cancelWarmTransfer() {
    if (!this.conversation) {
      return;
    }

    let { url, params } = this.getWarmTransferUrlAndParams('cancel');
    await post(url, params);

    this.currentCallAction = undefined;
    this.addingCallParticipantInfo = undefined;
    this.resetWarmTransferState();
    this.isOnHold = true;
  }

  async completeWarmTransfer() {
    if (!this.conversation) {
      return;
    }

    let { url, params } = this.getWarmTransferUrlAndParams('complete');
    await post(url, params);

    this.notifyTransferSuccess(this.addingCallParticipantInfo?.label);
    this.currentCallAction = undefined;
    this.addingCallParticipantInfo = undefined;
    this.resetWarmTransferState();
    this.isOnHold = false;
  }

  async teammateLeaveConference() {
    if (!this.conversation) {
      return;
    }

    await post('/ember/conference_calls/leave', {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation.id,
    });
  }

  async teammateEndConferenceCall() {
    if (!this.conversation) {
      return;
    }

    await post('/ember/conference_calls/end_conference_call', {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation.id,
    });
  }

  async removeAdminFromCall() {
    if (!this.conversation || !this.incomingCall) {
      return;
    }

    await post('/ember/phone_call/remove_admin_from_call', {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation.id,
    });
  }

  tearDownCallbackModal() {
    this.shouldNotifyTeammate = false;
    this.callbackAudio.pause();
    this.callbackAudio.currentTime = 0;
  }

  async acceptCallback(conversationId?: number | undefined, selectedOutboundNumber?: string) {
    let data = await get('/ember/phone_call/callback', {
      app_id: this.session.workspace.id,
      conversation_id: conversationId || this.conversation?.id,
    });

    let json = (await data.conversation) as ConversationWireFormat;
    this.conversation = Conversation.deserialize(json);

    this.userId = this.conversation?.firstParticipant.id;

    if (!this.conversation) {
      return;
    }

    this.tearDownCallbackIgnoreTimer();
    this.tearDownCallbackModal();

    await this.intercomCallService.getWorkspacePhoneNumbers();
    // Priority: 1) Agent's explicit dropdown selection, 2) backend cascade resolution, 3) original inbound number
    let outboundNumber =
      selectedOutboundNumber || data.resolved_outbound_number || data.call.workspace_phone_number;
    let dialingNumber =
      this.intercomCallService.workspacePhoneNumbers.findBy('phoneNumber', outboundNumber)
        ?.phoneNumber || this.intercomCallService.workspacePhoneNumbers.firstObject?.phoneNumber;

    if (this.session.workspace?.isFeatureEnabled('pii-attribute-masking')) {
      // PII-safe path: backend resolves the phone number from UserId
      await this.callStoredUser(this.conversation, this.userSummary!, dialingNumber);
    } else {
      let number = this.conversation.user?.phone as string;
      await this.callNumber(number, this.conversation, this.userSummary!, dialingNumber);
    }
    this.acceptedCallback = true;
    this.callbackAdminId = undefined;
  }

  async closeCallback(conversationId: number) {
    await post('/ember/phone_call/close_callback', {
      app_id: this.session.workspace.id,
      conversation_id: conversationId,
    });
  }

  async ignoreCallback() {
    this.tearDownCallbackIgnoreTimer();
    this.tearDownCallbackModal();
    await post('/ember/phone_call/ignore_callback', {
      app_id: this.session.workspace.id,
      conversation_id: this.conversation!.id,
      admin_id: this.callbackAdminId,
    });
    this.callbackAdminId = undefined;
  }

  async checkAdminMicrophonePermissions() {
    try {
      // Mozilla does not support querying generically for microphone permissions. Instead Firefox has an expectation that
      // an end user should have more granular control over which device is accessed https://github.com/mozilla/standards-positions/issues/19
      // Therefor if this block of code throws we should just skip the check for now.
      let microphonePermission = 'microphone' as PermissionName;
      let permission = await navigator.permissions.query({ name: microphonePermission });
      this.adminLacksMicrophonePermissions = permission.state === 'denied';
      this.handlePermissionChangedEvent(permission);
    } catch (error) {
      this.logTwilioEvent('permissions-exception', { error });
    }
  }

  handlePermissionChangedEvent(permission: PermissionStatus) {
    permission.onchange = ({ target }: Event) => {
      let updatedPermission = target as PermissionStatus;
      this.adminLacksMicrophonePermissions = updatedPermission.state !== 'granted';

      if (this.noPermissionNotification) {
        this.snackbar.clearNotification(this.noPermissionNotification);
      }
    };
  }

  navigateToConversation() {
    if (!this.conversation) {
      return;
    }

    let inbox = this.inboxState.activeInbox || {
      id: InboxType.All,
      category: InboxCategory.Shared,
    };

    this.router.transitionTo(
      'inbox.workspace.inbox.inbox.conversation.conversation',
      inbox.category,
      inbox.id,
      this.conversation.id,
    );
  }

  async recordEvent(action: string, conversationId: number, duration?: number | null) {
    let customer = this.customerService.customer;

    let payload: any = {
      action,
      object: 'conversation',
      place: 'inbox2',
      conversation_id: conversationId,
      is_trial: customer?.hasActiveTrials,
    };

    if (!isNone(duration)) {
      payload.duration = duration;
    }

    this.intercomEventService.trackAnalyticsEvent(payload);
  }

  tearDownCallbackIgnoreTimer() {
    if (this.callbackAcceptanceTimer !== null) {
      clearInterval(this.callbackAcceptanceTimer);
    }
  }

  setupAddingParticipantTimer() {
    this.addingParticipantTimer = setTimeout(() => {
      this.currentCallAction = undefined;
      this.addingCallParticipantInfo = undefined;
    }, NOTIFICATION_TIMEOUT);
  }

  tearDownAddingParticipantTimer() {
    if (!this.addingParticipantTimer) {
      return;
    }

    clearInterval(this.addingParticipantTimer);
    this.currentCallAction = undefined;
    this.addingCallParticipantInfo = undefined;
  }

  resetCallAttributes() {
    this.participants = [];
    this.conversation = undefined;
    this.userId = undefined;
    this.activeCall = undefined;
    this.dispatchCallingStateToReact();
    this.addingCallParticipantInfo = undefined;
    this.currentCallAction = undefined;
    this.isOnHold = false;
    this.incomingCallType = undefined;
    this.resolvedOutboundNumber = undefined;
    this.callerPhone = undefined;
    this.callerName = undefined;
    this.incomingConversationId = undefined;
    this.conversationLoadPromise = undefined;
    this.resetWarmTransferState();
    this.isRecordingEnabled = this.isRecordingEnabledInbound;
    this.isRecording = this.isRecordingEnabled;
    this.callIncomingTimestamp = undefined;
  }

  getToken() {
    return get('/ember/twilio/token', {
      app_id: this.session.workspace.id,
      admin_id: this.session.teammate.id,
    });
  }

  logTwilioEvent(eventType: string, eventData: any, error?: TwilioError) {
    this.logService.log({
      timestamp: moment().format(),
      event: 'twilio_event',
      event_type: eventType,
      event_data: eventData,
      error,
      errorCode: error?.code,
    });
  }

  get isCallRinging() {
    if (this.isWarmTransfer) {
      return this.warmTransferState === 'ringing';
    }

    return ['connecting', 'ringing'].includes(this.callState);
  }

  get isWarmTransferCallActive() {
    return this.isWarmTransfer && this.warmTransferState !== 'background-hold';
  }

  get isCallEnded() {
    return this.activeCall?.callState === 'closed';
  }

  startSendingDevicePresence() {
    this.devicePresenceStartTime = Date.now();
    // Don't set lastSuccessfulDevicePresence here - wait for actual successful ping
    this.setupDiagnosticsTracking();
    this.sendDevicePresencePeriodically();
    this.deviceStatusPoller = setInterval(() => this.sendDevicePresencePeriodically(), ONE_MINUTE);
    this.exposeDebugCommands();
  }

  stopSendingDevicePresence() {
    this.deviceStatusPoller && clearTimeout(this.deviceStatusPoller);
    this.destroyDevicePresence();
    this.clearDevicePresenceHealthNotification();
    this.lastSuccessfulDevicePresence = undefined;
    this.devicePresenceStartTime = undefined;
    this.cleanupDiagnosticsTracking();
    this.resetRecoveryState();
  }

  async sendDevicePresencePeriodically() {
    if (!navigator.onLine) {
      this.logTwilioEvent('heartbeat-skipped-offline', {});
      return;
    }

    try {
      if (this.device && this.device.state === 'registered') {
        await this.sendDevicePresence();
      } else {
        this.logTwilioEvent('device-not-registered', {
          device: this.device,
          state: this.device?.state,
        });
        this.attemptDeviceRecovery();
      }
    } catch (error) {
      this.logTwilioEvent('send-device-presence-error', { error });
    } finally {
      this.checkDevicePresenceHealth();
    }
  }

  async sendDevicePresence() {
    let now = Date.now();
    let context = this.buildHeartbeatContext(now);

    // Track hidden heartbeats
    if (document.hidden) {
      this.consecutiveHiddenHeartbeats++;
    }

    try {
      await post('/ember/twilio_device_presence', {
        app_id: this.session.workspace.id,
        admin_id: this.session.teammate.id,
      });

      this.lastSuccessfulDevicePresence = now;
      this.devicePresenceSuccessCount++;
      let hadFailures = this.consecutiveFailures > 0;
      this.consecutiveFailures = 0;
      this.lastDevicePresenceError = undefined;

      // Send diagnostics for notable successes (recovery or hidden tab)
      if (this.isDiagnosticsEnabled() && (hadFailures || document.hidden)) {
        this.sendDiagnosticEvent('heartbeat_success', {
          ...context,
          recovered_from_failures: hadFailures,
        });
      }
    } catch (err: unknown) {
      this.devicePresenceFailureCount++;
      this.consecutiveFailures++;
      this.lastDevicePresenceError = this.categorizeError(err);

      let errorMessage = err instanceof Error ? err.message : 'Unknown error';

      this.logTwilioEvent('heartbeat-error', {
        err,
        errorCategory: this.lastDevicePresenceError,
        consecutiveFailures: this.consecutiveFailures,
        ...context,
      });

      // Send diagnostics for failures
      if (this.isDiagnosticsEnabled()) {
        this.sendDiagnosticEvent('heartbeat_failure', {
          ...context,
          error_category: this.lastDevicePresenceError,
          error_message: errorMessage,
        });
      }

      throw err;
    }
  }

  destroyDevicePresence() {
    ajaxDelete('/ember/twilio_device_presence', {
      app_id: this.session.workspace.id,
      admin_id: this.session.teammate.id,
    }).catch((err: any) => {
      this.logTwilioEvent('heartbeat-error', { err });
      throw err;
    });
  }

  checkDevicePresenceHealth() {
    if (!this.devicePresenceStartTime) {
      return;
    }

    let now = Date.now();
    let timeSinceStart = now - this.devicePresenceStartTime;
    let timeSinceLastPing = this.lastSuccessfulDevicePresence
      ? now - this.lastSuccessfulDevicePresence
      : timeSinceStart;

    // Unhealthy if:
    // 1. We've been running for 2+ minutes and never sent a ping successfully, OR
    // 2. We've sent pings before but it's been 2+ minutes since the last successful one
    let isUnhealthy = timeSinceLastPing > TWO_MINUTES;

    // Only show notification if admin is available AND has phone channel availability
    let shouldShowNotification =
      isUnhealthy &&
      !this.devicePresenceHealthNotification &&
      !this.adminAwayService.awayModeEnabled &&
      (this.adminAwayService.channelAvailability === 'phone' ||
        this.adminAwayService.channelAvailability === 'both');

    let shouldClearNotification =
      (!isUnhealthy || !shouldShowNotification) && this.devicePresenceHealthNotification;

    if (shouldShowNotification) {
      this.devicePresenceHealthNotification = this.snackbar.notify(
        this.intl.t('calling.device-presence.unhealthy'),
        {
          type: 'error',
          persistent: true,
          clearable: true,
        },
      );
    } else if (shouldClearNotification && this.devicePresenceHealthNotification) {
      this.snackbar.clearNotification(this.devicePresenceHealthNotification);
      this.devicePresenceHealthNotification = undefined;
    }
  }

  clearDevicePresenceHealthNotification() {
    if (this.devicePresenceHealthNotification) {
      this.snackbar.clearNotification(this.devicePresenceHealthNotification);
      this.devicePresenceHealthNotification = undefined;
    }
  }

  private isTrialCallTermination(duration: number | null | undefined): boolean {
    if (!duration) {
      return false;
    }
    let isTrialWorkspace =
      this.intercomCallService.callingEventMetadata.free_usage_trial_ongoing === true;
    return isTrialWorkspace && duration >= 115 && duration <= 130;
  }

  private showTrialCallTerminationNotification() {
    this.snackbar.notify(this.intl.t('calling.trial.call-ended'), {
      type: 'warning',
      persistent: true,
      clearable: true,
    });
  }

  // Device presence diagnostics methods
  private setupDiagnosticsTracking() {
    if (this.diagnosticsListenersSetup || !this.isDiagnosticsEnabled()) {
      return;
    }
    this.diagnosticsListenersSetup = true;
    this.lastUserActivityAt = Date.now();

    // Track user activity events
    document.addEventListener('pointermove', this.onUserActivity);
    document.addEventListener('pointerdown', this.onUserActivity);
    document.addEventListener('keydown', this.onUserActivity);

    // Track tab visibility changes
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Track online/offline status
    window.addEventListener('online', this.onOnlineStatusChange);
    window.addEventListener('offline', this.onOnlineStatusChange);
  }

  private cleanupDiagnosticsTracking() {
    if (!this.diagnosticsListenersSetup) {
      return;
    }

    document.removeEventListener('pointermove', this.onUserActivity);
    document.removeEventListener('pointerdown', this.onUserActivity);
    document.removeEventListener('keydown', this.onUserActivity);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onOnlineStatusChange);
    window.removeEventListener('offline', this.onOnlineStatusChange);

    this.diagnosticsListenersSetup = false;
    this.devicePresenceSuccessCount = 0;
    this.devicePresenceFailureCount = 0;
    this.consecutiveFailures = 0;
    this.consecutiveHiddenHeartbeats = 0;

    // Clean up debug commands
    if (typeof window !== 'undefined') {
      delete (window as any).intercomPhoneDiagnostics;
    }
  }

  private onUserActivity = () => {
    let now = Date.now();
    if (this.lastUserActivityAt && now - this.lastUserActivityAt < 5000) {
      return;
    }
    this.lastUserActivityAt = now;
  };

  private onVisibilityChange = () => {
    let now = Date.now();
    if (document.hidden) {
      this.tabHiddenSince = now;
      if (this.isDiagnosticsEnabled()) {
        this.sendDiagnosticEvent('tab_visibility_change', {
          tab_hidden: true,
          last_activity_age_ms: this.lastUserActivityAt ? now - this.lastUserActivityAt : null,
        });
      }
    } else {
      let hiddenDuration = this.tabHiddenSince ? now - this.tabHiddenSince : null;
      if (this.isDiagnosticsEnabled()) {
        this.sendDiagnosticEvent('tab_visibility_change', {
          tab_hidden: false,
          tab_hidden_duration_ms: hiddenDuration,
          consecutive_hidden_heartbeats: this.consecutiveHiddenHeartbeats,
        });
      }
      this.tabHiddenSince = undefined;
      this.consecutiveHiddenHeartbeats = 0;
    }
  };

  private onOnlineStatusChange = () => {
    if (this.isDiagnosticsEnabled()) {
      this.sendDiagnosticEvent('network_status_change', {
        network_online: navigator.onLine,
      });
    }
  };

  private buildHeartbeatContext(now: number): Record<string, any> {
    return {
      timestamp: now,
      session_duration_ms: this.devicePresenceStartTime ? now - this.devicePresenceStartTime : null,
      heartbeat_number: this.devicePresenceSuccessCount + this.devicePresenceFailureCount + 1,
      last_activity_age_ms: this.lastUserActivityAt ? now - this.lastUserActivityAt : null,
      user_is_active: this.lastUserActivityAt ? now - this.lastUserActivityAt < 60000 : false,
      tab_hidden: document.hidden,
      tab_hidden_duration_ms: this.tabHiddenSince ? now - this.tabHiddenSince : null,
      consecutive_hidden_heartbeats: this.consecutiveHiddenHeartbeats,
      network_online: navigator.onLine,
      connection_type: this.getConnectionType(),
      device_state: this.device?.state,
      time_since_last_success_ms: this.lastSuccessfulDevicePresence
        ? now - this.lastSuccessfulDevicePresence
        : null,
      consecutive_failures: this.consecutiveFailures,
      success_count: this.devicePresenceSuccessCount,
      failure_count: this.devicePresenceFailureCount,
      away_mode_enabled: this.adminAwayService.awayModeEnabled,
      away_status_reason: this.adminAwayService.awayStatusReason,
    };
  }

  private getConnectionType(): string | null {
    let nav = navigator as any;
    if (nav.connection) {
      return nav.connection.effectiveType || nav.connection.type || null;
    }
    return null;
  }

  private categorizeError(err: unknown): string {
    if (!navigator.onLine) {
      return 'network_offline';
    }
    // Type narrow for ajax error responses
    let ajaxError = err as { jqXHR?: { status?: number } } | undefined;
    if (ajaxError?.jqXHR?.status === 0) {
      return 'network_error';
    }
    if (ajaxError?.jqXHR?.status === 401) {
      return 'auth_error';
    }
    if (ajaxError?.jqXHR?.status === 429) {
      return 'rate_limited';
    }
    if (ajaxError?.jqXHR?.status !== undefined && ajaxError.jqXHR.status >= 500) {
      return 'server_error';
    }
    return 'unknown';
  }

  private isDiagnosticsEnabled(): boolean {
    return this.session.workspace?.isFeatureEnabled('phone-device-presence-diagnostics') ?? false;
  }

  private isCallModalDiagnosticsEnabled(): boolean {
    return (
      this.session.workspace?.isFeatureEnabled('phone-incoming-call-modal-diagnostics') ?? false
    );
  }

  private async sendDiagnosticEvent(eventType: string, data: Record<string, any>) {
    try {
      await post('/ember/twilio_device_presence_diagnostics', {
        app_id: this.session.workspace.id,
        admin_id: this.session.teammate.id,
        event_type: eventType,
        ...data,
      });
    } catch {
      // Silently fail - diagnostics should not impact main functionality
    }
  }

  private exposeDebugCommands() {
    if (typeof window !== 'undefined') {
      (window as any).intercomPhoneDiagnostics = {
        getStatus: () => this.getDiagnosticStatus(),
        forceHeartbeat: () => this.sendDevicePresence(),
        getAwayModeState: () => ({
          enabled: this.adminAwayService?.awayModeEnabled,
          reason: this.adminAwayService?.awayStatusReason,
          channelAvailability: this.adminAwayService?.channelAvailability,
        }),
        sendDiagnosticSummary: () => this.sendDiagnosticSummary(),
      };
    }
  }

  private getDiagnosticStatus() {
    let now = Date.now();
    return {
      devicePresent:
        !!this.lastSuccessfulDevicePresence && now - this.lastSuccessfulDevicePresence < 120000,
      lastSuccessfulHeartbeat: this.lastSuccessfulDevicePresence
        ? new Date(this.lastSuccessfulDevicePresence).toISOString()
        : null,
      secondsSinceLastHeartbeat: this.lastSuccessfulDevicePresence
        ? Math.round((now - this.lastSuccessfulDevicePresence) / 1000)
        : null,
      consecutiveFailures: this.consecutiveFailures,
      successCount: this.devicePresenceSuccessCount,
      failureCount: this.devicePresenceFailureCount,
      successRate: this.calculateSuccessRate(),
      tabHidden: document.hidden,
      networkOnline: navigator.onLine,
      deviceState: this.device?.state,
      lastError: this.lastDevicePresenceError,
      sessionDuration: this.devicePresenceStartTime
        ? Math.round((now - this.devicePresenceStartTime) / 1000)
        : null,
      awayModeEnabled: this.adminAwayService?.awayModeEnabled,
      diagnosticsEnabled: this.isDiagnosticsEnabled(),
    };
  }

  private calculateSuccessRate(): string {
    let total = this.devicePresenceSuccessCount + this.devicePresenceFailureCount;
    if (total === 0) {
      return '100%';
    }
    return `${Math.round((this.devicePresenceSuccessCount / total) * 100)}%`;
  }

  private async sendDiagnosticSummary() {
    if (!this.isDiagnosticsEnabled()) {
      return { error: 'Diagnostics not enabled' };
    }
    let context = this.buildHeartbeatContext(Date.now());
    await this.sendDiagnosticEvent('diagnostic_summary', context);
    return this.getDiagnosticStatus();
  }

  // Call quality diagnostics methods
  private isCallQualityDiagnosticsEnabled(): boolean {
    return this.session.workspace?.isFeatureEnabled('phone-call-quality-diagnostics') ?? false;
  }

  willDestroy() {
    this.tearDownCallbackIgnoreTimer();
    this.tearDownAddingParticipantTimer();
    this.clearDevicePresenceHealthNotification();
    this.resetRecoveryState();

    this.deviceStatusPoller && clearInterval(this.deviceStatusPoller);
    this.windowFocusOnlineAbortController && this.windowFocusOnlineAbortController.abort();
  }

  async manualAnswer(callId: string) {
    // Disable the incoming call ringer
    this.device?.audio?.incoming(false);
    await this.intercomCallService.setAdminOnCall();
    this.isTabManuallyAnswering = true;

    try {
      await post('/ember/phone_call/manual_answer', {
        app_id: this.session.workspace.id,
        admin_id: this.session.teammate.id,
        call_id: callId,
      });
    } catch (response) {
      switch (response.jqXHR.status) {
        case 409:
        case 423:
        case 422:
          this.snackbar.notifyError(this.intl.t('calling.manual-answer.error.call-in-progress'), {
            timeout: MANUAL_ANSWER_NOTIFICATION_TIMEOUT,
          });
          break;
        default:
          this.snackbar.notifyError(this.intl.t('calling.manual-answer.error.catch-all'), {
            timeout: MANUAL_ANSWER_NOTIFICATION_TIMEOUT,
          });
          break;
      }
      this.adminAwayService.setAdminAsAvailable();
      this.device?.audio?.incoming(true);
      this.isTabManuallyAnswering = false;
    }
  }

  get isActiveCall() {
    return this.activeCall !== undefined;
  }

  dispatchCallingStateToReact() {
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('calling:twilio-state-changed', {
      isInitialized: this.isInitialized,
      isActiveCall: this.isActiveCall,
    });
  }

  get callState() {
    return this.activeCall?.callState || 'connecting';
  }

  async getAudioDevices(): Promise<{
    microphone: string | null;
    speaker: string | null;
  }> {
    try {
      if (!navigator.mediaDevices || !('enumerateDevices' in navigator.mediaDevices)) {
        return { microphone: null, speaker: null };
      }

      let devices = await navigator.mediaDevices.enumerateDevices();
      let audioInputDevices = devices.filter((device) => device.kind === 'audioinput');
      let audioOutputDevices = devices.filter((device) => device.kind === 'audiooutput');

      // Get the default microphone (usually the first one or marked as default)
      let defaultMicrophone =
        audioInputDevices.find(
          (device) => device.deviceId === 'default' || device.label.includes('Default'),
        ) || audioInputDevices[0];

      // Get the default speaker (usually the first one or marked as default)
      let defaultSpeaker =
        audioOutputDevices.find(
          (device) => device.deviceId === 'default' || device.label.includes('Default'),
        ) || audioOutputDevices[0];

      return {
        microphone: defaultMicrophone?.label || null,
        speaker: defaultSpeaker?.label || null,
      };
    } catch (error) {
      this.logTwilioEvent('failed-to-get-audio-devices', { error });
      return { microphone: null, speaker: null };
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    twilioService: TwilioService;
    'twilio-service': TwilioService;
  }
}
